import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Context from 'managers/Context';
import useDisableCreateSalesOrders from 'hooks/useDisableCreateSalesOrders';
import { SALESORDERS } from 'constants/Entities';
import { SAGE_STATUS_WIDGET } from 'constants/Features';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { getLiteral } from 'utils/getLiteral';
import { getSmartFieldProps } from 'utils/fm/fields';
import { successToast, errorToast } from 'utils/toast';
import CommentsWidget from 'containers/components/widgets/CommentsWidget';
import StatusPipelineWidget from 'containers/components/widgets/StatusPipelineWidget';
import SageStatusPipelineWidget from 'containers/components/widgets/SageStatusPipelineWidget';
import SalesOrderDetailWidget from '../SalesOrdersDetail/widgets/SalesOrderDetailWidget';
import SalesOrderProductLinesWidget from '../SalesOrdersDetail/widgets/SalesOrderProductLinesWidget';
import SalesOrdersBottomInfoPanel, {
    useSalesOrdersBottomInfoPanelHeight,
} from '../components/SalesOrdersBottomInfoPanel';
import ExtraFieldsWidget from 'containers/components/widgets/ExtraFields';
import { getReadOnlyOptions } from 'containers/components/EntityDetail/getReadOnlyOptions';

import { SalesService } from 'services';

const useSalesOrdersDetail = () => {
    const [panelWidth, setPanelWidth] = useState('');
    const { config: { permission, standardFieldsSchemaMap, vendorLicense } = {} } =
        Context.store.getState();
    const { activeDetailEntity, hasSageStatusWidget, useTaxesSalesOrders, salesOrderShippingCost } =
        useSelector((state) => ({
            activeDetailEntity: state.entityDetail[state.entityDetail.active]?.entity || null,
            hasSageStatusWidget: state.config.flagsmith?.[SAGE_STATUS_WIDGET] || false,
            useTaxesSalesOrders: state?.config?.userData?.useTaxesSalesOrders || false,
            salesOrderShippingCost: state?.config?.userData?.salesOrderShippingCost === true,
        }));
    const bottomInfoPanelHeight = useSalesOrdersBottomInfoPanelHeight();

    const disableCreateSalesOrders = useDisableCreateSalesOrders();
    const crudPermissions = permission?.crud_permission?.[SALESORDERS.permission] || null;
    const duplicatePermission = permission?.duplicateSalesOrders;
    const standardFieldsConfiguration =
        standardFieldsSchemaMap?.[SALESORDERS.extraFieldName] || null;
    const commentFieldReadOnly = standardFieldsConfiguration?.['comments']?.readOnly;
    const internalCommentReadOnly = standardFieldsConfiguration?.['internalComments']?.readOnly;
    const showStateWidget = !standardFieldsConfiguration?.idEstadoSalesOrder?.isHidden || false;

    const handleResize = useCallback(() => {
        let width = window.innerWidth - 350;
        if (width > 1200) width = 1200;
        setPanelWidth(`${width}px`);
    }, []);

    useEffect(() => {
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const checkStatusType = useCallback((status) => {
        if (!status) return '';
        if (getBackendBoolean(status.win)) return 'win';
        if (getBackendBoolean(status.lost)) return 'lost';
        return '';
    }, []);

    const getWidgets = useCallback(
        ({ data, widgets, setSize, configMap, mappedSchema }) => {
            const isSageProduct = vendorLicense?.toLowerCase()?.includes('sage');
            const StatusComponent =
                isSageProduct && hasSageStatusWidget
                    ? SageStatusPipelineWidget
                    : StatusPipelineWidget;

            widgets = [
                {
                    id: 'SalesOrderDetailWidget',
                    widget: (
                        <SalesOrderDetailWidget
                            data={data}
                            setSize={setSize('SalesOrderDetailWidget')}
                        />
                    ),
                    x: 0,
                    y: 0,
                    width: 2,
                    height: 300,
                    fillColumn: false,
                },
                {
                    id: 'StatusComponent',
                    widget: (
                        <StatusComponent
                            entity={SALESORDERS}
                            data={data}
                            getChangeLog={SalesService.getSalesOrdersStatusChangeLog}
                            checkStatusType={checkStatusType}
                            fieldId="idStateSalesOrder"
                            fieldName="stateSalesOrder"
                            listId="tblEstadosSalesOrders"
                            setSize={setSize('StatusComponent')}
                            isReadOnly={
                                (mappedSchema &&
                                    getSmartFieldProps({
                                        field: mappedSchema?.idEstadoSalesOrder,
                                        configMap,
                                        data,
                                    })?.readOnly) ||
                                data.readOnly ||
                                standardFieldsConfiguration?.idEstadoSalesOrder?.readOnly
                            }
                            dependencyParentField={
                                standardFieldsConfiguration?.idEstadoSalesOrder?.strParentField
                            }
                        />
                    ),
                    x: 2,
                    y: 0,
                    width: 2,
                    height: 276,
                },
                {
                    id: 'SalesOrdersInternalCommentsWidget',
                    widget: (
                        <CommentsWidget
                            title={getLiteral('label_internalcomment')}
                            data={data}
                            comments={data?.internalComments}
                            setSize={setSize('SalesOrdersInternalCommentsWidget')}
                            entity={SALESORDERS}
                            field="internalComments"
                            isReadOnly={
                                (mappedSchema &&
                                    getSmartFieldProps({
                                        field: mappedSchema?.internalComments,
                                        configMap,
                                        data,
                                    })?.readOnly) ||
                                !crudPermissions?.update ||
                                getBackendBoolean(data?.readOnly) ||
                                internalCommentReadOnly
                            }
                        />
                    ),
                    x: 2,
                    y: 0,
                    width: 2,
                    height: 256,
                },
                {
                    id: 'SalesOrderProductLinesWidget',
                    widget: (
                        <SalesOrderProductLinesWidget
                            data={data}
                            products={data.products || []}
                            setSize={setSize('SalesOrderProductLinesWidget')}
                        />
                    ),
                    x: 0,
                    y: 1,
                    width: 4,
                    height: 200,
                    fillColumn: false,
                },
                {
                    id: 'SalesOrdersCommentsWidget',
                    widget: (
                        <CommentsWidget
                            title={getLiteral('label_comment_salesorders')}
                            data={data}
                            comments={data?.comments}
                            setSize={setSize('SalesOrdersCommentsWidget')}
                            entity={SALESORDERS}
                            field="comments"
                            isReadOnly={
                                (mappedSchema &&
                                    getSmartFieldProps({
                                        field: mappedSchema?.comments,
                                        configMap,
                                        data,
                                    })?.readOnly) ||
                                !crudPermissions?.update ||
                                getBackendBoolean(data?.readOnly) ||
                                commentFieldReadOnly
                            }
                        />
                    ),
                    x: 0,
                    y: 2,
                    width: 2,
                    height: 256,
                },
                {
                    id: 'SalesOrdersExtraFieldsWidget',
                    widget: (
                        <ExtraFieldsWidget
                            entityType={SALESORDERS}
                            entityId={data?.id}
                            entity={data}
                            setSize={setSize('SalesOrdersExtraFieldsWidget')}
                            config={{ height: 256 }}
                        />
                    ),
                    x: 2,
                    y: 2,
                    width: 2,
                    height: 256,
                },
                ...widgets,
            ];

            return widgets;
        },
        [
            checkStatusType,
            commentFieldReadOnly,
            crudPermissions?.update,
            hasSageStatusWidget,
            internalCommentReadOnly,
            standardFieldsConfiguration?.idEstadoSalesOrder?.readOnly,
            standardFieldsConfiguration?.idEstadoSalesOrder?.strParentField,
            vendorLicense,
        ],
    );

    const getWidgetsPermissions = useCallback(
        (data, permissions) => {
            return {
                StatusPipelineWidget: showStateWidget,
                SalesOrdersCommentsWidget: !standardFieldsConfiguration?.comments?.isHidden,
                SalesOrdersInternalCommentsWidget:
                    !standardFieldsConfiguration?.internalComments?.isHidden,
                SalesOrdersExtraFieldsWidget: permissions?.extraFieldsSalesOrders !== false,
                4: false,
                30: false,
                9:
                    (permissions?.documentManagement && permissions?.documentos_salesorders) ||
                    false,
                28:
                    (permissions?.documentManagement && permissions?.documentos_salesorders) ||
                    false,
                10: (permissions?.informes && permissions?.reports_salesorders) || false,
                29: (permissions?.informes && permissions?.reports_salesorders) || false,
            };
        },
        [
            showStateWidget,
            standardFieldsConfiguration?.comments?.isHidden,
            standardFieldsConfiguration?.internalComments?.isHidden,
        ],
    );

    const getInitialStateWidgets = useCallback(() => {
        return {
            SalesOrderDetailWidget: {
                height: 300,
                isEmitted: false,
            },
            SalesOrderProductLinesWidget: {
                height: 200,
                maxHeight: 780,
                isEmitted: false,
            },
            SalesOrdersCommentsWidget: {
                height: 256,
                maxHeight: 512,
                isEmitted: false,
            },
            SalesOrdersExtraFieldsWidget: {
                height: 256,
                maxHeight: 512,
                isEmitted: false,
            },
            SalesOrdersInternalCommentsWidget: {
                height: 256,
                maxHeight: 512,
                isEmitted: false,
            },
            StatusPipelineWidget: {
                height: 256,
                maxHeight: 512,
                isEmitted: false,
            },
        };
    }, []);

    const { options, optionsParams } = useMemo(() => {
        let options = [];
        if (duplicatePermission) options.push('duplicate');
        options.push('follow');
        let optionsParams = [undefined, 'followingItem'];

        options.push('edit');
        if (crudPermissions?.delete) options.push('delete');
        return { options, optionsParams };
    }, [crudPermissions?.delete, duplicatePermission]);

    const onDelete = useCallback(() => {
        successToast({
            text: getLiteral('succes_entitydeletedsuccessfully'),
        });
    }, []);

    const onErrorDelete = useCallback(() => {
        errorToast({
            text: getLiteral('label_failed_delete'),
        });
    }, []);

    const onFollow = useCallback((bool) => {
        successToast({
            text: bool ? getLiteral('succes_following') : getLiteral('succes_unfollowing'),
        });
    }, []);

    const onErrorFollow = useCallback(() => {
        errorToast({
            text: getLiteral('error_following_account'),
        });
    }, []);

    const getInfo = useCallback(
        (data) => {
            if (!data?.warning) return null;
            if (!permission.warnings) return null;
            if (!permission.editWarnings && !permission.viewWarningsInSalesOrders) return null;
            return data.warning;
        },
        [permission.editWarnings, permission.viewWarningsInSalesOrders, permission.warnings],
    );

    const getOptions = useCallback(
        (data) => {
            if (!data || !Object.keys(data)?.length) return [];
            return getReadOnlyOptions({
                options,
                data,
                crudPermissions,
                extraDisable: disableCreateSalesOrders,
            });
        },
        [disableCreateSalesOrders, options, crudPermissions],
    );

    const bottomPanel = useMemo(() => {
        return {
            className: 'fm-sales-orders-bottom-panel',
            height: bottomInfoPanelHeight,
            render: ({ data }) => {
                if (activeDetailEntity !== SALESORDERS.entity) return null;

                return (
                    <SalesOrdersBottomInfoPanel
                        data={data}
                        showTaxDetails={useTaxesSalesOrders}
                        showShippingCost={salesOrderShippingCost}
                    />
                );
            },
        };
    }, [activeDetailEntity, bottomInfoPanelHeight, useTaxesSalesOrders, salesOrderShippingCost]);

    return {
        className: 'fm-sales-orders-detail',
        bottomPanel,
        entity: SALESORDERS,
        getWidgets,
        getWidgetsPermissions,
        getInitialStateWidgets,
        getOptions,
        optionsParams,
        onDelete,
        onErrorDelete,
        getInfo,
        onFollow,
        onErrorFollow,
        customWidth: panelWidth,
    };
};

export default useSalesOrdersDetail;
